/**
 * ediClient — the Phase 22 billing-edi HttpApi (contract §6).
 *
 * A thin wrapper over apiClient: the same Cognito Authorization header, 401 refresh-and-retry,
 * timeout and ApiError handling, but against REACT_APP_EDI_API_BASE_URL. apiClient sends an
 * absolute endpoint as-is, so the wrapper only prefixes the EDI base URL.
 *
 * The base URL is optional at startup (it is needed only when the tenant's `billingEdi` flag is
 * on); a call made without it throws ApiError with domain code EDI_NOT_CONFIGURED.
 */

import { apiClient } from './api-client';
import { ediApiConfig } from '../config/app.config';
import { ApiError, ApiErrorCode } from '../dtos/common/api.dto';
import type { ApiResponse, QueryParams, RequestOptions } from '../dtos/common/api.dto';

export const EDI_NOT_CONFIGURED = 'EDI_NOT_CONFIGURED';

/** True when this build knows the billing-edi API address. */
export function isEdiConfigured(): boolean {
  return ediApiConfig.baseUrl.length > 0;
}

function ediUrl(path: string): string {
  if (!isEdiConfigured()) {
    throw new ApiError(
      'Pelora Billing is not configured for this environment.',
      ApiErrorCode.UNKNOWN,
      0,
      undefined,
      EDI_NOT_CONFIGURED
    );
  }
  return `${ediApiConfig.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

export const ediClient = {
  async get<T>(path: string, params?: QueryParams, options?: RequestOptions): Promise<ApiResponse<T>> {
    return apiClient.get<T>(ediUrl(path), params, options);
  },
  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return apiClient.post<T>(ediUrl(path), body, options);
  },
  async put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return apiClient.put<T>(ediUrl(path), body, options);
  },
};

export default ediClient;
